import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  title: string
  description?: string
  action?: ReactNode
  className?: string
  /**
   * The Studio route (`StudioRoute` in `@/store/types`) this heading is the
   * canonical page heading for. Required, not optional: the Studio
   * dashboard shell's route-change focus effect
   * (`studio-dashboard-shell.tsx`) matches `[data-route-heading="<route>"]`
   * against the CURRENT route exactly, not merely "a marked heading" — a
   * route-agnostic marker would match the SOURCE page's still-mounted
   * heading during the stale window between a route change and the
   * destination's own commit, focusing the page the user just left (a
   * regression, not a fix). Typed as a bare `string` rather than
   * `StudioRoute` to keep this shared, generic component decoupled from
   * the Studio-specific route union.
   */
  route: string
}

export function PageHeader({ title, description, action, className, route }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div className="min-w-0">
        <h1 data-route-heading={route} className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  )
}
